import React, { useState, useEffect, useMemo } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { 
  BarChart3, 
  Calendar, 
  ArrowDownUp, 
  Download, 
  Layers, 
  TrendingUp, 
  DollarSign, 
  Search, 
  Eye,
  Zap,
  ShoppingCart,
  Car,
  Milk,
  Carrot,
  Beef,
  Leaf,
  Sparkles
} from 'lucide-react';
import { expensesService } from '../services/supabaseService';
import { isPersonalCategory } from '../utils/expenseCategories';
import Modal from '../components/Modal';

// 7 Key Operational Benchmark Categories
const KEY_BENCHMARK_CATEGORIES = [
  {
    id: 'utilities',
    name: 'Utilities',
    match: (cat) => cat.toLowerCase() === 'utilities',
    icon: Zap,
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.3)'
  },
  {
    id: 'groceries',
    name: 'Groceries',
    match: (cat) => cat.toLowerCase() === 'groceries',
    icon: ShoppingCart,
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.3)'
  },
  {
    id: 'transportation',
    name: 'Transportation',
    match: (cat) => cat.toLowerCase() === 'transportation',
    icon: Car,
    color: '#0284c7',
    bg: 'rgba(2, 132, 199, 0.12)',
    border: 'rgba(2, 132, 199, 0.3)'
  },
  {
    id: 'dairy_products',
    name: 'Dairy Products',
    match: (cat) => cat.toLowerCase() === 'dairy products',
    icon: Milk,
    color: '#06b6d4',
    bg: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.3)'
  },
  {
    id: 'vegetables',
    name: 'Vegetables',
    match: (cat) => cat.toLowerCase() === 'vegetables',
    icon: Carrot,
    color: '#f97316',
    bg: 'rgba(249, 115, 22, 0.12)',
    border: 'rgba(249, 115, 22, 0.3)'
  },
  {
    id: 'meat',
    name: 'Meat',
    match: (cat) => cat.toLowerCase() === 'meat',
    icon: Beef,
    color: '#f43f5e',
    bg: 'rgba(244, 63, 94, 0.12)',
    border: 'rgba(244, 63, 94, 0.3)'
  },
  {
    id: 'leafy_vegetables',
    name: 'Leafy Vegetables',
    match: (cat) => cat.toLowerCase() === 'leafy vegetables',
    icon: Leaf,
    color: '#84cc16',
    bg: 'rgba(132, 204, 22, 0.12)',
    border: 'rgba(132, 204, 22, 0.3)'
  }
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const getRankBadgeStyle = (rank) => {
  if (rank === 1) {
    return {
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: '#fff',
      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
    };
  }
  if (rank === 2) {
    return {
      background: 'linear-gradient(135deg, #94a3b8, #64748b)',
      color: '#fff',
      boxShadow: '0 2px 8px rgba(148, 163, 184, 0.3)'
    };
  }
  if (rank === 3) {
    return {
      background: 'linear-gradient(135deg, #b45309, #78350f)',
      color: '#fff',
      boxShadow: '0 2px 8px rgba(180, 83, 9, 0.3)'
    };
  }
  return {
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'var(--text-secondary, #94a3b8)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };
};

const MonthlyCategoryBreakdown = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & State (persistent with useSessionStorage)
  const [selectedYear, setSelectedYear] = useSessionStorage('catbreakdown_year', new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useSessionStorage('catbreakdown_month', 'all'); // 'all' or 1-12
  const [categoryScope, setCategoryScope] = useSessionStorage('catbreakdown_scope', 'business'); // 'business', 'personal', 'all'
  const [sortDirection, setSortDirection] = useSessionStorage('catbreakdown_sort', 'desc'); // 'desc' (Top to Low) or 'asc'
  const [searchTerm, setSearchTerm] = useSessionStorage('catbreakdown_search', '');
  
  // Drill-down inspection modal
  const [inspectModal, setInspectModal] = useState({ open: false, category: '', month: null, items: [] });

  useEffect(() => {
    const fetchAllExpenses = async () => {
      try {
        setLoading(true);
        const data = await expensesService.getAll();
        setExpenses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching expenses for breakdown:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllExpenses();
  }, []);

  // Filter expenses by selected scope and year
  const filteredExpenses = useMemo(() => {
    const yr = parseInt(selectedYear, 10);
    return expenses.filter(e => {
      if (!e.expense_date) return false;
      const d = parseDateSafe(e.expense_date);
      if (!d || d.getFullYear() !== yr) return false;

      const isPers = isPersonalCategory(e.category);
      if (categoryScope === 'business' && isPers) return false;
      if (categoryScope === 'personal' && !isPers) return false;

      return true;
    });
  }, [expenses, selectedYear, categoryScope]);

  // Aggregate by Month & Category
  const monthlyData = useMemo(() => {
    // 12 months array
    const months = Array.from({ length: 12 }, (_, mIdx) => {
      return {
        monthIndex: mIdx,
        monthNumber: mIdx + 1,
        monthName: MONTH_NAMES[mIdx],
        shortName: MONTH_SHORT[mIdx],
        totalAmount: 0,
        transactionCount: 0,
        categories: {} // categoryName: { name, amount, count, maxAmount, items: [] }
      };
    });

    filteredExpenses.forEach(exp => {
      const d = parseDateSafe(exp.expense_date);
      if (!d) return;
      const mIdx = d.getMonth();
      const amt = Number(exp.amount || 0);
      const cat = (exp.category || 'Uncategorized').trim();

      months[mIdx].totalAmount += amt;
      months[mIdx].transactionCount += 1;

      if (!months[mIdx].categories[cat]) {
        months[mIdx].categories[cat] = {
          name: cat,
          amount: 0,
          count: 0,
          maxAmount: 0,
          items: []
        };
      }

      months[mIdx].categories[cat].amount += amt;
      months[mIdx].categories[cat].count += 1;
      if (amt > months[mIdx].categories[cat].maxAmount) {
        months[mIdx].categories[cat].maxAmount = amt;
      }
      months[mIdx].categories[cat].items.push(exp);
    });

    // Convert categories map to sorted array (Top to Low or Low to Top)
    return months.map(m => {
      let catList = Object.values(m.categories).map(c => ({
        ...c,
        percentage: m.totalAmount > 0 ? (c.amount / m.totalAmount) * 100 : 0,
        average: c.count > 0 ? c.amount / c.count : 0
      }));

      // Search filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        catList = catList.filter(c => c.name.toLowerCase().includes(query));
      }

      // Sort Top to Low (desc) or Low to Top (asc)
      catList.sort((a, b) => {
        return sortDirection === 'desc' ? b.amount - a.amount : a.amount - b.amount;
      });

      return {
        ...m,
        categoryList: catList
      };
    });
  }, [filteredExpenses, searchTerm, sortDirection]);

  // Overall Year Aggregate per Category
  const annualCategoryData = useMemo(() => {
    const catMap = {};
    let grandTotal = 0;
    let totalTransactions = 0;

    filteredExpenses.forEach(exp => {
      const amt = Number(exp.amount || 0);
      const cat = (exp.category || 'Uncategorized').trim();
      const d = parseDateSafe(exp.expense_date);
      const mIdx = d ? d.getMonth() : -1;

      grandTotal += amt;
      totalTransactions += 1;

      if (!catMap[cat]) {
        catMap[cat] = {
          name: cat,
          amount: 0,
          count: 0,
          maxAmount: 0,
          monthlyTotals: new Array(12).fill(0),
          items: []
        };
      }

      catMap[cat].amount += amt;
      catMap[cat].count += 1;
      if (amt > catMap[cat].maxAmount) {
        catMap[cat].maxAmount = amt;
      }
      if (mIdx >= 0) {
        catMap[cat].monthlyTotals[mIdx] += amt;
      }
      catMap[cat].items.push(exp);
    });

    let catList = Object.values(catMap).map(c => ({
      ...c,
      percentage: grandTotal > 0 ? (c.amount / grandTotal) * 100 : 0,
      average: c.count > 0 ? c.amount / c.count : 0
    }));

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      catList = catList.filter(c => c.name.toLowerCase().includes(query));
    }

    catList.sort((a, b) => {
      return sortDirection === 'desc' ? b.amount - a.amount : a.amount - b.amount;
    });

    return {
      grandTotal,
      totalTransactions,
      categories: catList
    };
  }, [filteredExpenses, searchTerm, sortDirection]);

  // Top Category of Selected Period
  const topCategory = annualCategoryData.categories.length > 0 ? annualCategoryData.categories[0] : null;

  // Highest Month
  const highestMonth = useMemo(() => {
    let topM = null;
    monthlyData.forEach(m => {
      if (!topM || m.totalAmount > topM.totalAmount) {
        topM = m;
      }
    });
    return topM && topM.totalAmount > 0 ? topM : null;
  }, [monthlyData]);

  // Key Operational Categories Benchmark Calculations (Utilities, Groceries, Transportation, Dairy, Vegetables, Meat, Leafy Veg)
  const benchmarkData = useMemo(() => {
    const yr = parseInt(selectedYear, 10);
    const selMonthNum = selectedMonth !== 'all' ? parseInt(selectedMonth, 10) : null;

    // Active operating months in selected year with expenses
    const activeMonthsSet = new Set();
    expenses.forEach(e => {
      if (!e.expense_date) return;
      const d = parseDateSafe(e.expense_date);
      if (d && d.getFullYear() === yr) {
        activeMonthsSet.add(d.getMonth());
      }
    });
    const activeMonthsCount = activeMonthsSet.size > 0 ? activeMonthsSet.size : 1;

    let combinedYearTotal = 0;
    let combinedMonthSpend = 0;

    const cards = KEY_BENCHMARK_CATEGORIES.map(cfg => {
      let yearTotal = 0;
      let monthSpend = 0;
      let monthItemCount = 0;
      let yearItemCount = 0;
      const matchedMonthItems = [];

      expenses.forEach(e => {
        if (!e.expense_date) return;
        const d = parseDateSafe(e.expense_date);
        if (!d || d.getFullYear() !== yr) return;

        const catName = (e.category || '').trim();
        if (cfg.match(catName)) {
          const amt = Number(e.amount || 0);
          yearTotal += amt;
          yearItemCount += 1;

          if (selMonthNum !== null && d.getMonth() + 1 === selMonthNum) {
            monthSpend += amt;
            monthItemCount += 1;
            matchedMonthItems.push(e);
          }
        }
      });

      const monthlyAvg = activeMonthsCount > 0 ? yearTotal / activeMonthsCount : 0;
      const diffFromAvg = selMonthNum !== null ? monthSpend - monthlyAvg : 0;
      const pctDiff = monthlyAvg > 0 ? Math.round((diffFromAvg / monthlyAvg) * 100) : 0;

      combinedYearTotal += yearTotal;
      combinedMonthSpend += monthSpend;

      return {
        ...cfg,
        yearTotal,
        yearItemCount,
        monthlyAvg,
        monthSpend,
        monthItemCount,
        diffFromAvg,
        pctDiff,
        matchedMonthItems
      };
    });

    const combinedMonthlyAvg = activeMonthsCount > 0 ? combinedYearTotal / activeMonthsCount : 0;
    const combinedDiff = selMonthNum !== null ? combinedMonthSpend - combinedMonthlyAvg : 0;
    const combinedPctDiff = combinedMonthlyAvg > 0 ? Math.round((combinedDiff / combinedMonthlyAvg) * 100) : 0;

    return {
      activeMonthsCount,
      combinedYearTotal,
      combinedMonthlyAvg,
      combinedMonthSpend,
      combinedDiff,
      combinedPctDiff,
      cards
    };
  }, [expenses, selectedYear, selectedMonth]);

  // Handle Inspect Drill-down
  const handleInspectCategory = (categoryName, monthName, items) => {
    setInspectModal({
      open: true,
      category: categoryName,
      month: monthName,
      items: items || []
    });
  };

  // CSV Export
  const handleExportCSV = () => {
    const rows = [
      ['Monthly Category Breakdown Report'],
      ['Year', selectedYear],
      ['Scope', categoryScope.toUpperCase()],
      ['Generated On', new Date().toLocaleDateString()],
      [],
      ['Month', 'Rank', 'Category', 'Amount (Rs)', 'Percentage (%)', 'Transaction Count', 'Avg per Transaction (Rs)']
    ];

    monthlyData.forEach(m => {
      if (selectedMonth !== 'all' && parseInt(selectedMonth, 10) !== m.monthNumber) return;

      m.categoryList.forEach((cat, idx) => {
        rows.push([
          m.monthName,
          idx + 1,
          `"${cat.name.replace(/"/g, '""')}"`,
          cat.amount.toFixed(2),
          cat.percentage.toFixed(1) + '%',
          cat.count,
          cat.average.toFixed(2)
        ]);
      });
      if (m.categoryList.length > 0) {
        rows.push([m.monthName + ' Total', '', '', m.totalAmount.toFixed(2), '100%', m.transactionCount, '']);
        rows.push([]);
      }
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Category_Breakdown_${selectedYear}_${categoryScope}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered view data based on selectedMonth
  const displayedMonths = useMemo(() => {
    if (selectedMonth === 'all') {
      return monthlyData;
    }
    const mNum = parseInt(selectedMonth, 10);
    return monthlyData.filter(m => m.monthNumber === mNum);
  }, [monthlyData, selectedMonth]);

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: 'var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }} />
        <p>Loading monthly category breakdown...</p>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '3rem' }}>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: '700', margin: 0 }}>Category Breakdown by Month</h2>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              padding: '0.3rem 0.75rem',
              borderRadius: '9999px',
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              color: '#818cf8',
              border: '1px solid rgba(99, 102, 241, 0.3)'
            }}>
              Top to Low Ranking
            </span>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>
            Monthly totals for each expense category ordered from highest to lowest spend.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center flex-wrap" style={{ gap: '0.75rem' }}>
          <button 
            className="btn-primary flex items-center gap-2" 
            onClick={handleExportCSV}
            style={{ padding: '0.45rem 0.9rem', fontSize: '0.875rem' }}
          >
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* Control Bar: Filters & Sorters */}
      <div className="card mb-6" style={{ padding: '1.25rem' }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          
          {/* Left filters: Scope, Year, Month */}
          <div className="flex items-center flex-wrap gap-3">
            {/* Scope Tabs */}
            <div style={{
              display: 'flex',
              backgroundColor: 'rgba(0,0,0,0.25)',
              borderRadius: '0.5rem',
              padding: '0.25rem',
              border: '1px solid var(--border-color)'
            }}>
              <button
                onClick={() => setCategoryScope('business')}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '0.375rem',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: categoryScope === 'business' ? 'var(--primary-color)' : 'transparent',
                  color: categoryScope === 'business' ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.2s ease'
                }}
              >
                Business (PG)
              </button>
              <button
                onClick={() => setCategoryScope('personal')}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '0.375rem',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: categoryScope === 'personal' ? '#ec4899' : 'transparent',
                  color: categoryScope === 'personal' ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.2s ease'
                }}
              >
                Personal
              </button>
              <button
                onClick={() => setCategoryScope('all')}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '0.375rem',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: categoryScope === 'all' ? '#8b5cf6' : 'transparent',
                  color: categoryScope === 'all' ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.2s ease'
                }}
              >
                All Categories
              </button>
            </div>

            {/* Year Selector */}
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem'
              }}
            >
              {[...Array(6)].map((_, i) => {
                const yr = new Date().getFullYear() - i;
                return <option key={yr} value={yr} style={{ color: '#000' }}>{yr}</option>;
              })}
            </select>

            {/* Month Selector */}
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem'
              }}
            >
              <option value="all" style={{ color: '#000' }}>All 12 Months (Grid View)</option>
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1} style={{ color: '#000' }}>{name}</option>
              ))}
            </select>

            {/* Sort Order Toggle */}
            <button
              onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-2"
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
              title="Toggle Sort Order"
            >
              <ArrowDownUp size={14} color="var(--primary-color)" />
              <span>{sortDirection === 'desc' ? 'Top to Low' : 'Low to Top'}</span>
            </button>
          </div>

          {/* Right: Search Filter */}
          <div style={{ position: 'relative', minWidth: '220px' }}>
            <Search 
              size={15} 
              style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} 
            />
            <input
              type="text"
              placeholder="Search category..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.45rem 0.75rem 0.45rem 2.2rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                fontSize: '0.85rem'
              }}
            />
          </div>

        </div>
      </div>

      {/* Top 4 KPI Summary Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {/* Total Spend */}
        <div className="card flex items-center gap-5" style={{ padding: '1.25rem', borderTop: '3px solid var(--primary-color)' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--primary-color)', borderRadius: '0.5rem' }}>
            <DollarSign size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>
              {selectedMonth === 'all' ? `Total Spend (${selectedYear})` : `${MONTH_NAMES[parseInt(selectedMonth, 10) - 1]} Spend`}
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              ₹{(selectedMonth === 'all' ? annualCategoryData.grandTotal : (displayedMonths[0]?.totalAmount || 0)).toLocaleString()}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
              {selectedMonth === 'all' ? `${annualCategoryData.totalTransactions} transactions` : `${displayedMonths[0]?.transactionCount || 0} transactions`}
            </div>
          </div>
        </div>

        {/* #1 Top Category */}
        <div className="card flex items-center gap-5" style={{ padding: '1.25rem', borderTop: '3px solid #f59e0b' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderRadius: '0.5rem' }}>
            <BarChart3 size={22} />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>#1 Top Category</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f59e0b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {topCategory ? topCategory.name : 'N/A'}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
              {topCategory ? `₹${topCategory.amount.toLocaleString()} (${topCategory.percentage.toFixed(1)}%)` : 'No data'}
            </div>
          </div>
        </div>

        {/* Highest Expense Month */}
        <div className="card flex items-center gap-5" style={{ padding: '1.25rem', borderTop: '3px solid #10b981' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderRadius: '0.5rem' }}>
            <TrendingUp size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>Highest Spend Month</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {highestMonth ? highestMonth.monthName : 'N/A'}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#10b981', marginTop: '0.15rem' }}>
              {highestMonth ? `₹${highestMonth.totalAmount.toLocaleString()}` : 'No data'}
            </div>
          </div>
        </div>

        {/* Active Categories Count */}
        <div className="card flex items-center gap-5" style={{ padding: '1.25rem', borderTop: '3px solid #8b5cf6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6', borderRadius: '0.5rem' }}>
            <Layers size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>Active Categories</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {annualCategoryData.categories.length}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
              {categoryScope === 'business' ? 'Business PG Categories' : categoryScope === 'personal' ? 'Personal Categories' : 'All Combined'}
            </div>
          </div>
        </div>
      </div>

      {/* KEY OPERATIONAL CATEGORIES MONTHLY AVERAGE BENCHMARK (Utilities, Groceries, Transportation, Dairy, Vegetables, Meat, Leafy Veg) */}
      <div className="card mb-8" style={{
        padding: '1.5rem',
        border: '1px solid var(--border-color)',
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.45) 0%, rgba(15, 23, 42, 0.65) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle decorative accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #f59e0b 0%, #10b981 25%, #0284c7 50%, #06b6d4 75%, #f43f5e 100%)'
        }} />

        {/* Section Header */}
        <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              color: 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Sparkles size={20} />
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <span>Operational Categories — Monthly Average Benchmarks</span>
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '9999px',
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  color: 'var(--primary-color)',
                  border: '1px solid rgba(99, 102, 241, 0.3)'
                }}>
                  {selectedYear}
                </span>
                {selectedMonth !== 'all' && (
                  <span style={{
                    fontSize: '0.72rem',
                    fontWeight: '700',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    color: '#10b981',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    vs {MONTH_NAMES[parseInt(selectedMonth, 10) - 1]} Actuals
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
                Average monthly spend calculated across {benchmarkData.activeMonthsCount} active operating month{benchmarkData.activeMonthsCount > 1 ? 's' : ''} in {selectedYear}
                {selectedMonth !== 'all' ? ` • Comparing against ${MONTH_NAMES[parseInt(selectedMonth, 10) - 1]} ${selectedYear} records` : ''}
              </p>
            </div>
          </div>

          {/* Combined 7-Category summary pill */}
          <div style={{
            padding: '0.5rem 0.9rem',
            borderRadius: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.85rem'
          }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Combined 7-Category Avg
              </div>
              <div style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--accent-color)' }}>
                ₹{Math.round(benchmarkData.combinedMonthlyAvg).toLocaleString()}<span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>/mo</span>
              </div>
            </div>
            {selectedMonth !== 'all' && (
              <div style={{ borderLeft: '1px solid rgba(255, 255, 255, 0.1)', paddingLeft: '0.85rem' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {MONTH_SHORT[parseInt(selectedMonth, 10) - 1]} Spend
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                  ₹{Math.round(benchmarkData.combinedMonthSpend).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 7-Card Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
          gap: '0.85rem'
        }}>
          {benchmarkData.cards.map(cat => {
            const isSelectedMonth = selectedMonth !== 'all';
            const isAboveAvg = isSelectedMonth && cat.monthSpend > cat.monthlyAvg;
            const hasNoSpendInMonth = isSelectedMonth && cat.monthSpend === 0;

            return (
              <div
                key={cat.id}
                style={{
                  padding: '1rem',
                  borderRadius: '0.65rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.22)',
                  border: `1px solid ${cat.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (isSelectedMonth && cat.matchedMonthItems.length > 0) {
                    handleInspectCategory(cat.name, MONTH_NAMES[parseInt(selectedMonth, 10) - 1], cat.matchedMonthItems);
                  } else {
                    setSearchTerm(cat.name);
                  }
                }}
                title={isSelectedMonth && cat.matchedMonthItems.length > 0 ? "Click to inspect this month's expense items" : "Click to filter list below"}
              >
                {/* Header: Icon + Category Name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden' }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '7px',
                    backgroundColor: cat.bg,
                    color: cat.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <cat.icon size={16} />
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      {cat.name}
                    </div>
                  </div>
                </div>

                {/* Main Metric: Monthly Average */}
                <div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Monthly Average
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '800', color: cat.color, marginTop: '0.15rem', lineHeight: 1.2 }}>
                    ₹{Math.round(cat.monthlyAvg).toLocaleString()}
                    <span style={{ fontSize: '0.72rem', fontWeight: '500', color: 'var(--text-secondary)', marginLeft: '0.2rem' }}>/mo</span>
                  </div>
                </div>

                {/* Comparison or Annual Context */}
                <div style={{
                  paddingTop: '0.55rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                  fontSize: '0.72rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.3rem'
                }}>
                  {isSelectedMonth ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>{MONTH_SHORT[parseInt(selectedMonth, 10) - 1]} Actual:</span>
                        <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                          ₹{cat.monthSpend.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Variance:</span>
                        <span style={{
                          fontWeight: '700',
                          padding: '0.1rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.68rem',
                          backgroundColor: hasNoSpendInMonth 
                            ? 'rgba(148, 163, 184, 0.1)' 
                            : isAboveAvg 
                              ? 'rgba(245, 158, 11, 0.15)' 
                              : 'rgba(16, 185, 129, 0.15)',
                          color: hasNoSpendInMonth 
                            ? 'var(--text-secondary)' 
                            : isAboveAvg 
                              ? '#f59e0b' 
                              : '#10b981',
                          border: `1px solid ${hasNoSpendInMonth ? 'rgba(148, 163, 184, 0.2)' : isAboveAvg ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`
                        }}>
                          {hasNoSpendInMonth
                            ? 'No spend'
                            : `${cat.monthSpend >= cat.monthlyAvg ? '+' : '-'}₹${Math.abs(Math.round(cat.diffFromAvg)).toLocaleString()} (${cat.pctDiff > 0 ? `+${cat.pctDiff}%` : `${cat.pctDiff}%`})`}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Annual Total:</span>
                      <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                        ₹{Math.round(cat.yearTotal).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* MONTH CARDS GRID: Showing each month's categories ranked Top to Low */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 style={{ fontSize: '1.2rem', fontWeight: '600', margin: 0 }}>
            {selectedMonth === 'all' ? `Monthly Breakdown (${selectedYear})` : `${MONTH_NAMES[parseInt(selectedMonth, 10) - 1]} ${selectedYear} Details`}
          </h3>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            Showing {displayedMonths.length} month{displayedMonths.length > 1 ? 's' : ''} • Ordered by {sortDirection === 'desc' ? 'Highest Spend' : 'Lowest Spend'}
          </span>
        </div>

        {/* 12 Months Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedMonth === 'all' 
            ? 'repeat(auto-fill, minmax(340px, 1fr))' 
            : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.25rem'
        }}>
          {selectedMonth === 'all' ? (
            displayedMonths.map(month => {
              const hasData = month.categoryList.length > 0;
              return (
                <div 
                  key={month.monthNumber} 
                  className="card" 
                  style={{
                    padding: '1.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: hasData ? '1px solid var(--border-color)' : '1px dashed rgba(255,255,255,0.08)',
                    opacity: hasData ? 1 : 0.65,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                >
                  {/* Card Header */}
                  <div>
                    <div className="flex justify-between items-center pb-3 mb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center gap-2">
                        <Calendar size={18} color="var(--primary-color)" />
                        <span style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {month.monthName}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: hasData ? 'var(--accent-color)' : 'var(--text-secondary)' }}>
                          ₹{month.totalAmount.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                          {month.transactionCount} entries
                        </div>
                      </div>
                    </div>

                    {/* Category List Ranked Top to Low */}
                    {!hasData ? (
                      <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        No expenses recorded
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '340px', overflowY: 'auto', paddingRight: '0.25rem' }}>
                        {month.categoryList.map((cat, idx) => {
                          const rank = idx + 1;
                          const badgeStyle = getRankBadgeStyle(rank);
                          return (
                            <div 
                              key={cat.name}
                              style={{
                                padding: '0.6rem 0.75rem',
                                borderRadius: '0.5rem',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                border: '1px solid rgba(255,255,255,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.35rem',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease'
                              }}
                              onClick={() => handleInspectCategory(cat.name, month.monthName, cat.items)}
                              title="Click to inspect individual expense records"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2" style={{ overflow: 'hidden' }}>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    ...badgeStyle
                                  }}>
                                    {rank}
                                  </span>
                                  <span style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {cat.name}
                                  </span>
                                </div>
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <span style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                                    ₹{cat.amount.toLocaleString()}
                                  </span>
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                                    ({cat.percentage.toFixed(1)}%)
                                  </span>
                                </div>
                              </div>

                              {/* Progress bar for share */}
                              <div style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${cat.percentage}%`,
                                  height: '100%',
                                  backgroundColor: rank === 1 ? '#f59e0b' : rank === 2 ? '#6366f1' : 'var(--accent-color)',
                                  borderRadius: '2px'
                                }} />
                              </div>

                              <div className="flex justify-between items-center" style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                                <span>{cat.count} transaction{cat.count > 1 ? 's' : ''}</span>
                                <span>Avg: ₹{Math.round(cat.average).toLocaleString()}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Card Footer Summary */}
                  {hasData && (
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <span>{month.categoryList.length} Categories</span>
                      <span style={{ color: '#818cf8', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                        Top: {month.categoryList[0]?.name}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            /* Single Month View: Large Category Cards Ranked Top to Low */
            displayedMonths[0]?.categoryList.map((cat, idx) => {
              const rank = idx + 1;
              const badgeStyle = getRankBadgeStyle(rank);
              return (
                <div 
                  key={cat.name} 
                  className="card"
                  style={{
                    padding: '1.25rem',
                    borderTop: rank === 1 ? '3px solid #f59e0b' : rank === 2 ? '3px solid #6366f1' : '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onClick={() => handleInspectCategory(cat.name, displayedMonths[0].monthName, cat.items)}
                >
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          padding: '0.2rem 0.55rem',
                          borderRadius: '9999px',
                          ...badgeStyle
                        }}>
                          #{rank} Rank
                        </span>
                      </div>
                      <span style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px'
                      }}>
                        {cat.percentage.toFixed(1)}% of Month
                      </span>
                    </div>

                    <h4 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
                      {cat.name}
                    </h4>

                    <div style={{ fontSize: '1.5rem', fontWeight: '800', color: rank === 1 ? '#f59e0b' : 'var(--text-primary)', marginBottom: '0.75rem' }}>
                      ₹{cat.amount.toLocaleString()}
                    </div>

                    {/* Progress bar */}
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        width: `${cat.percentage}%`,
                        height: '100%',
                        backgroundColor: rank === 1 ? '#f59e0b' : rank === 2 ? '#6366f1' : 'var(--accent-color)',
                        borderRadius: '3px'
                      }} />
                    </div>

                    {/* Metrics Breakdown */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.5rem',
                      padding: '0.6rem',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem'
                    }}>
                      <div>
                        <div style={{ color: 'var(--text-secondary)' }}>Transactions</div>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)', marginTop: '0.1rem' }}>{cat.count}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-secondary)' }}>Average / Entry</div>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)', marginTop: '0.1rem' }}>₹{Math.round(cat.average).toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-secondary)' }}>Max Single Entry</div>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)', marginTop: '0.1rem' }}>₹{cat.maxAmount.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-secondary)' }}>Scope</div>
                        <div style={{ fontWeight: '700', color: isPersonalCategory(cat.name) ? '#ec4899' : '#10b981', marginTop: '0.1rem' }}>
                          {isPersonalCategory(cat.name) ? 'Personal' : 'Business'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.3rem', fontSize: '0.75rem', color: 'var(--primary-color)' }}>
                    <Eye size={13} />
                    <span>View {cat.count} Entries</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ANNUAL CATEGORY RANKINGS CARD */}
      <div className="card" style={{ padding: '1.5rem' }}>
        <div className="flex justify-between items-center mb-5 flex-wrap gap-4">
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
              Full Year {selectedYear} Category Summary (Ranked High to Low)
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
              Cumulative total spent across all 12 months for {categoryScope.toUpperCase()} categories.
            </p>
          </div>
          <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--primary-color)' }}>
            Total Spend: ₹{annualCategoryData.grandTotal.toLocaleString()}
          </div>
        </div>

        {annualCategoryData.categories.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No category data available for the selected filters.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {annualCategoryData.categories.map((cat, idx) => {
              const rank = idx + 1;
              const badgeStyle = getRankBadgeStyle(rank);
              return (
                <div
                  key={cat.name}
                  style={{
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleInspectCategory(cat.name, `All ${selectedYear}`, cat.items)}
                  title="Click to view all entries for this category"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...badgeStyle
                      }}>
                        {rank}
                      </span>
                      <span style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                        {cat.name}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--text-primary)' }}>
                        ₹{cat.amount.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        {cat.percentage.toFixed(1)}% of Year
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    width: '100%',
                    height: '5px',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${cat.percentage}%`,
                      height: '100%',
                      backgroundColor: rank === 1 ? '#f59e0b' : rank === 2 ? '#6366f1' : 'var(--accent-color)',
                      borderRadius: '3px'
                    }} />
                  </div>

                  {/* Monthly Mini Bar Sparkline */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', height: '24px', gap: '3px', marginTop: '0.25rem' }}>
                    {cat.monthlyTotals.map((mAmt, mIdx) => {
                      const maxMonth = Math.max(...cat.monthlyTotals, 1);
                      const heightPercent = mAmt > 0 ? Math.max(15, (mAmt / maxMonth) * 100) : 4;
                      return (
                        <div
                          key={mIdx}
                          title={`${MONTH_SHORT[mIdx]}: ₹${mAmt.toLocaleString()}`}
                          style={{
                            flex: 1,
                            height: `${heightPercent}%`,
                            backgroundColor: mAmt > 0 ? (rank === 1 ? '#f59e0b' : 'var(--primary-color)') : 'rgba(255,255,255,0.05)',
                            borderRadius: '1px'
                          }}
                        />
                      );
                    })}
                  </div>

                  <div className="flex justify-between items-center" style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                    <span>{cat.count} total records</span>
                    <span>Avg/entry: ₹{Math.round(cat.average).toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Drill-down Expense Inspection Modal */}
      {inspectModal.open && (
        <Modal
          isOpen={inspectModal.open}
          onClose={() => setInspectModal({ open: false, category: '', month: null, items: [] })}
          title={`Expense Entries: ${inspectModal.category} (${inspectModal.month})`}
        >
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem',
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              fontSize: '0.85rem'
            }}>
              <span>Total Entries: <strong>{inspectModal.items.length}</strong></span>
              <span>Total Amount: <strong style={{ color: 'var(--accent-color)' }}>₹{inspectModal.items.reduce((s, i) => s + Number(i.amount || 0), 0).toLocaleString()}</strong></span>
            </div>

            <table className="table" style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subcategory / Item</th>
                  <th>Amount (₹)</th>
                  <th>Description / Notes</th>
                </tr>
              </thead>
              <tbody>
                {inspectModal.items
                  .sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date))
                  .map((item, i) => (
                    <tr key={item.id || i}>
                      <td>{item.expense_date ? item.expense_date.split('T')[0] : 'N/A'}</td>
                      <td>
                        <span style={{ fontWeight: '600' }}>{item.subcategory || item.item_name || 'General'}</span>
                      </td>
                      <td style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                        ₹{Number(item.amount || 0).toLocaleString()}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.description || item.notes || '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MonthlyCategoryBreakdown;